import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';

import { Operations } from '../graphql/FamilyTree.generated';
import { useUpdateFamilyTreeNodeSettingsMutation } from '../graphql/FamilyTreeMutations.generated';
import {
  FamilyTreeEffectFn,
  FamilyTreeOnAfterEditOptions,
  SaveNodeConfigEffect,
} from '../types';

export function useSaveNodeConfigEffect({
  onAfterEdit,
}: FamilyTreeOnAfterEditOptions = {}): FamilyTreeEffectFn<SaveNodeConfigEffect> {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const [updateNodeSettings] = useUpdateFamilyTreeNodeSettingsMutation({
    onError: (error) => {
      showFeedback('Unable to save family tree settings. Please try again.');
      reportError('Error saving family tree node settings', error);
    },
    onCompleted: () => {
      // In presentation/design mode, reload the presentation so the slide
      // reflects the updated visibility. awaitRefetchQueries (below) makes this
      // fire only after the family tree refetch completes, so the slide
      // re-measures against fresh layout rather than stale positions. No-op
      // elsewhere.
      onAfterEdit?.();
    },
  });

  return useCallback<FamilyTreeEffectFn<SaveNodeConfigEffect>>(
    (_state, effect, _dispatch) => {
      updateNodeSettings({
        refetchQueries: [Operations.Query.GetHouseholdFamilyTree],
        awaitRefetchQueries: true,
        variables: {
          clientProfileId: effect.clientProfileId,
          settings: {
            showIndividualOnTree: effect.config.showTile,
            showNotesForIndividual: effect.config.showNotes,
          },
        },
      });
    },
    [updateNodeSettings]
  );
}
